import React, { useContext } from 'react';
import styled from 'styled-components';
import { uniqueId } from 'lodash-es';
import { useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useAppDispatch } from '@/store';
import { DataElementContext } from '../common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useProcessList } from '../utils/useProcessList';
import { fetchSources } from '../../store/slices/dataSources';

const defaultProps = {
  className: '',
  styleText: `
  width: 100%;
  height: auto;
  `,
  properties: {
    dsType: '',
    deId: '',
  },
};

interface DataSourceProps {
  className: string;
  $styleText: string;
  style?: any;
  properties?: {
    dsType: string;
    deId: string;
  };
  children?: JSX.Element;
}

const DataSourceDiv = styled.div<DataSourceProps>((props) => props.$styleText);

export const DataSource = (componentProps: any) => {
  let props = componentProps;

  const ref = React.useRef<any>(null);

  const dataElementContext = useContext(DataElementContext);
  let isVisible = true;

  [props, isVisible] = processComponentProps(props, dataElementContext);

  const [__id] = React.useState(uniqueId('unique-'));
  const { i18n } = useTranslation();
  const dataSourcesItems = useAppSelector((state) => state.dataSources.items);
  const authenticationToken = useAppSelector((state) => state.authentication.access_token);
  const fetched = React.useRef('');
  const dispatch = useAppDispatch();

  let dsId = props.properties?.dsId ?? '';

  const filterKey = props.properties.key ?? null;
  let filterValue = props.properties.value ?? null;
  const valueFromParam = props.properties.valueFromParam ?? false;
  const matches = useMatches();

  if (valueFromParam && props?.properties?.pathParamKey) {
    if (matches && matches.length) {
      const match = matches[0];
      let pathParamKey = props.properties.pathParamKey;
      if (pathParamKey != null && pathParamKey[0] === ':') {
        pathParamKey = pathParamKey.slice(1);
      }
      if (match.params && match.params[pathParamKey] != null) {
        if (filterKey) {
          filterValue = match.params[pathParamKey];
        } else {
          dsId = match.params[pathParamKey];
        }
      }
    }
  }

  let loading = true;
  let loaded = false;

  React.useEffect(() => {
    if (authenticationToken && dsId && !dataSourcesItems[dsId] && fetched.current !== dsId) {
      fetched.current = dsId;
      dispatch(fetchSources({ ids: [dsId] }));
    }
  }, [authenticationToken, componentProps.properties.dsId, dsId, dataSourcesItems, dispatch]);

  if (dsId && dataSourcesItems[dsId]) {
    loading = false;
    loaded = true;
  }

  const { data: ds } = useProcessList(
    dataSourcesItems[dsId],
    i18n.language,
    dsId,
    props.properties.items && !isNaN(Number(props.properties.items)) ? Number(props.properties.items) : 0,
  );

  const contextValue = React.useMemo(() => {
    if (ds.length === 0) {
      const parent = ref?.current?.closest('.parent-scroller');
      if (parent) {
        parent.classList.add('d-none');
      }
    } else {
      const parent = ref?.current?.closest('.parent-scroller');
      if (parent) {
        parent.classList.remove('d-none');
      }
    }

    return {
      ...dataElementContext,
      dsList:
        filterKey && filterValue
          ? ds?.filter?.((item: any) => {
              if (filterKey && filterValue) {
                return item[filterKey] === filterValue;
              }
              return true;
            })
          : ds,
      __loading: loading,
      __loaded: loaded,
      __id,
    };
  }, [ds, dataElementContext, __id, loading, loaded, filterKey, filterValue]);

  if (!isVisible) return null;

  const extraDataProps: any = {};
  if (props?.properties) {
    Object.keys(props?.properties).forEach((key: any) => {
      if (key.indexOf('data-') === 0) {
        extraDataProps[key] = props?.properties[key];
      }
    });
  }

  if (!props.properties['data-ds-id'] && dsId) {
    extraDataProps['data-ds-id'] = dsId;
  }

  return (
    <DataSourceDiv
      ref={ref}
      className={props.className ?? ''}
      $styleText={props.styleText}
      style={props.style}
      {...extraDataProps}
    >
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </DataSourceDiv>
  );
};
